import React, { useState, useEffect } from 'react';
import { css } from '@emotion/core';
import {
  DropdownField,
  EditIcon,
  Heading,
  Modal,
  NakedButton,
  PlusIcon,
  SecondaryButton,
  Text,
  TextareaField,
  TextField,
  TextLink,
  useControlledField,
  useModalControl,
} from '@weave/design-system';
import { theme } from '@weave/theme-original';
import { UnifyLocationNotes } from './unify-locations-table.types';

export interface AddEditNotesProps {
  locationId: string;
  notes?: UnifyLocationNotes;
  onNotesChange: (notes: UnifyLocationNotes) => void;
  readOnly?: boolean;
  disabled?: boolean;
}

const DEFAULT_NOTES: UnifyLocationNotes = {};

const DISCLAIMER_TYPE_OPTIONS = [
  'Phone Conversation with IT',
  'Talked with the office',
  'Sent Email',
];

export const AddEditNotes: React.FC<AddEditNotesProps> = ({
  locationId,
  notes = DEFAULT_NOTES,
  onNotesChange,
  readOnly = false,
  disabled = false,
}) => {
  const { modalProps, openModal, closeModal } = useModalControl();
  const [formNotes, setFormNotes] = useState<UnifyLocationNotes>(notes);

  useEffect(() => {
    setFormNotes(notes);
  }, [notes]);

  const handleOpenModal = () => {
    if (disabled) return;
    setFormNotes(notes);
    openModal();
  };

  const handleSave = () => {
    onNotesChange(formNotes);
    closeModal();
  };

  const disclaimerField = useControlledField({
    type: 'dropdown',
    value: formNotes.disclaimerTypeSent || '',
    onChange: (val) =>
      setFormNotes((prev) => ({ ...prev, disclaimerTypeSent: val || undefined })),
  });

  const routerField = useControlledField({
    type: 'text',
    value: formNotes.routerMakeAndModel || '',
    onChange: (val) =>
      setFormNotes((prev) => ({ ...prev, routerMakeAndModel: val || undefined })),
  });

  const experienceField = useControlledField({
    type: 'text',
    value: formNotes.officeExperience || '',
    onChange: (val) =>
      setFormNotes((prev) => ({ ...prev, officeExperience: val || undefined })),
  });

  const hasNotes = Boolean(
    notes &&
      (notes.disclaimerTypeSent?.trim() ||
        notes.routerMakeAndModel?.trim() ||
        notes.officeExperience?.trim())
  );

  const renderReadOnlyField = (label: string, value?: string) => (
    <div css={styles.readOnlyField}>
      <Text as="span" color="light" weight="bold" size="medium">
        {label}
      </Text>
      <Text as="span" color="default" size="large" css={styles.readOnlyValue}>
        {value?.trim() || '—'}
      </Text>
    </div>
  );

  return (
    <div>
      {readOnly ? (
        hasNotes ? (
          <TextLink
            size="medium"
            color="primary"
            weight="bold"
            onClick={handleOpenModal}
            aria-label="View Notes"
          >
            View Notes
          </TextLink>
        ) : (
          <Text size="medium" color="light">
            —
          </Text>
        )
      ) : hasNotes ? (
        <NakedButton
          onClick={handleOpenModal}
          disabled={disabled}
          css={[styles.editBtn, disabled && styles.disabledBtn]}
          aria-label="Edit Notes"
        >
          <EditIcon size={16} color={disabled ? 'disabled' : 'primary'} />
          <Text
            as="span"
            size="medium"
            color={disabled ? 'disabled' : 'primary'}
            weight="bold"
          >
            Edit Notes
          </Text>
        </NakedButton>
      ) : (
        <SecondaryButton
          onClick={handleOpenModal}
          disabled={disabled}
          aria-label="Add Notes"
          css={styles.addBtn}
        >
          <PlusIcon size={16} color={disabled ? 'disabled' : 'default'} />
          <Text
            as="span"
            size="medium"
            color={disabled ? 'disabled' : 'default'}
            weight="bold"
          >
            Add Notes
          </Text>
        </SecondaryButton>
      )}

      <Modal {...modalProps} maxWidth={600}>
        <Modal.Header onClose={closeModal}>
          {readOnly
            ? 'View Handoff Notes'
            : hasNotes
            ? 'Edit Handoff Notes'
            : 'Add Handoff Notes'}
        </Modal.Header>
        <Modal.Body css={styles.modalBody}>
          {readOnly ? (
            <>
              <Heading level={3} css={styles.sectionTitle}>
                Network Health Information
              </Heading>
              {renderReadOnlyField('Disclaimer Type Sent', notes.disclaimerTypeSent)}
              {renderReadOnlyField('Router Make and Model', notes.routerMakeAndModel)}

              <Heading level={3} css={styles.sectionTitle}>
                Office Experience
              </Heading>
              {renderReadOnlyField('Notes', notes.officeExperience)}
            </>
          ) : (
            <>
              <Heading level={3} css={styles.sectionTitle}>
                Network Health Information
              </Heading>
              <DropdownField
                {...disclaimerField}
                label="Disclaimer Type Sent"
                placeholder="Select disclaimer type"
                disabled={readOnly}
                name={`disclaimer-${locationId}`}
              >
                {DISCLAIMER_TYPE_OPTIONS.map((opt) => (
                  <DropdownField.Option key={opt} value={opt}>
                    {opt}
                  </DropdownField.Option>
                ))}
              </DropdownField>

              <TextField
                {...routerField}
                label="Router Make and Model"
                placeholder="i.e. Sonicwall, Netgear, Comcast"
                disabled={readOnly}
                name={`router-${locationId}`}
              />

              <Heading level={3} css={styles.sectionTitle}>
                Office Experience
              </Heading>
              <TextareaField
                {...experienceField}
                label="Please include any helpful notes and details below"
                placeholder="Enter notes..."
                disabled={readOnly}
                name={`experience-${locationId}`}
                containerCss={styles.textarea}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Actions
          showPrimary={!readOnly}
          primaryLabel="Save Notes"
          secondaryLabel={readOnly ? 'Close' : 'Cancel'}
          onPrimaryClick={handleSave}
          onSecondaryClick={closeModal}
        />
      </Modal>
    </div>
  );
};

const styles = {
  addBtn: css`
    border-radius: 4px !important;
    gap: ${theme.spacing(1)};
  `,
  editBtn: css`
    display: inline-flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(1, 2)} !important;
    cursor: pointer;
  `,
  disabledBtn: css`
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #fafafa;
  `,
  modalBody: css`
    padding: ${theme.spacing(2, 3)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  sectionTitle: css`
    font-size: 16px;
    font-weight: 700;
    margin: ${theme.spacing(1, 0, 0, 0)};
  `,
  textarea: css`
    textarea {
      min-height: 100px;
    }
  `,
  readOnlyField: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
    margin-bottom: ${theme.spacing(1)};
  `,
  readOnlyValue: css`
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.5;
  `,
};
